import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { DAIMI_SLUG } from '@/content/daimi'

/**
 * The Top 10 Trends 2026 page — its copy and its figures, apart from the component
 * that draws them.
 *
 * **The twentieth report with a designed page of its own, and the first that is
 * neither a cut of the maturity study nor a Future 2030 outlook.** Asked for on
 * 2026-08-28 in the same words as the ten outlook pages before it — *"referans alarak
 * ilgili sayfayı birebir tıpa tıp aynısını tasarla"* — as a five-part full-page capture
 * of digitopia.co's Top 10 Trends 2026 page, plus the three report mockups that page
 * carries. And, as on the eleven requests before it, the instruction that the page point
 * at the 2026 DAIMI report rather than the 2024 one: *"en aşağıda önceden konuştuğumuz
 * gibi 2024'e değil 2026 DAIMI report'a yönlendirelim"*.
 *
 * So the bands are the reference's, in the reference's order: hero → the compass card →
 * "From AI Chaos to Measured Value" → three recommendations → who it is for → what is
 * inside → the close. The two shared closing bands (`content/reportClosing.ts`) sit
 * where they sit on every other report page, and the maturity band is the one addition,
 * which is where the 2026 pointer lives.
 *
 * **This is not an outlook page, and the shape says so.** There is no DMI forecast
 * chart, no five predictions and no three imperatives — the reference has none of them.
 * What it has instead is an accordion of audiences and a bulleted contents list, so this
 * page is its own component rather than an eleventh copy of the outlook one.
 *
 * **Four things here are worth knowing before reading a word of it back.**
 *
 *  * **Four of the five audience blurbs are written here, not transcribed.** The
 *    reference capture shows the accordion with only *C-suite Executives* open, so that
 *    is the one sentence digitopia.co actually supplied. The other four panels are
 *    closed in the capture and their copy is not recoverable from it. Leaving them empty
 *    would render four accordion rows that open onto nothing, which reads as broken — so
 *    they are written in the register of the one that is real, and
 *    `TRENDS_AUDIENCE.check` says plainly which sentence is theirs and which four are
 *    ours.
 *  * **The three mockups are matched to slots by shape, not by instruction.** The
 *    reference renders all three image slots empty — the same way the Energy capture
 *    rendered its imperative photographs — so the portrait shot in a hand sits beside
 *    "From AI Chaos to Measured Value", the small on-a-table shot beside the audience
 *    accordion, and the wide two-copy mockup beside the contents list.
 *    `TRENDS_CHAOS.check` is where that is put to a reader.
 *  * **The hero runs the live page's own background plate, and it took a second request to
 *    get there.** It shipped with the words beside the report's cover — the shape the
 *    nineteen designed report pages before it use — and `TRENDS_INTRO.check` said the live
 *    hero instead runs the artwork full-bleed with the title over it. Later the same day
 *    somebody sent that artwork, untitled, and a capture of the live hero. So the hero is
 *    now the plate behind the words: `TRENDS_INTRO` has the reasoning and the page
 *    component has the two scrims that keep the headline readable over it.
 *  * **The maturity band is not on the live page at all.** It is here because the
 *    request asked for the 2026 pointer at the bottom, and because the page's own first
 *    recommendation is "Measure before you move: establish a clear baseline of your
 *    Digital and AI maturity" — which is the DAIMI report. `TRENDS_MATURITY.check`
 *    records that it is an addition.
 *
 * **Where the words come from.** Everything else is transcribed from the five captures,
 * which are full-page renders read down to fit — the *shape* of the page is certain and
 * the *wording* is a careful reading of body copy set a handful of pixels tall. Every
 * band that carries prose therefore carries a `check`, and `topTrends2026Checks()` feeds
 * them into the same manifest so a production build cannot ship them unread.
 *
 * **What the live page has that this page cannot.** Its Download buttons and its closing
 * band open an embedded HubSpot form — first and last name, business email, company, job
 * title, phone, country, industry, a marketing opt-in and the privacy statement. These
 * documents are prerendered with no client bundle at all (`src/prerender.tsx`), so a
 * form script would never run, and the other thirty reports on this site hand the reader
 * the PDF from our own bucket instead. Every Download button here is that PDF, and
 * `TRENDS_CLOSE.check` is what keeps the gating question open.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * The slug digitopia.co uses, and the one the request named. It was already in `REPORTS`
 * before this page existed, so the page is built under it as it stands.
 */
export const TRENDS_SLUG = 'top-10-trends-2026'

/**
 * The hero.
 *
 * **The one band on this page that does not follow the designed-report shape, and it is
 * the answer to the question the first version of this file asked.** It shipped with the
 * words beside the report's cover, like the nineteen designed report pages before it, and
 * `check` said plainly that the live hero instead runs the artwork full-bleed with the
 * title over it. On 2026-08-28 that was answered by sending the artwork itself — the
 * untitled background plate, lifted off the live page — plus a capture of the live hero:
 * *"top 10 trends 2026 sayfasını açtığımızda arkadaki ana görsel"*. So the hero is now
 * that plate behind the words rather than the cover beside them.
 *
 * `photo` is therefore the **background**, not an illustration: it is drawn `object-cover`
 * across the whole band, so nothing in it is guaranteed to be visible at a given width and
 * no word of the page may live inside it. That is the reason it is the plate without the
 * lettering and not `top-10-trends-2026-cover.webp` — the cover carries "TOP 10 TRENDS
 * 2026" as pixels, and cropping a headline is not an option. The cover still opens the
 * report's card on the index; this page's own title is the `h1`.
 *
 * `lede` is the line the live hero prints in blue under the title. It is the report's
 * subtitle rather than a claim, so it is set as one rather than bolded like the outlook
 * pages' opening claims.
 */
export const TRENDS_INTRO = {
  lede: 'From AI Chaos to Measured Value: Navigating the New Complexity',
  body: [
    'Top 10 Trends 2026 is Digitopia Impact Labs’ read on the shifts that will decide which organizations turn AI spending into measured value in the year ahead, and which keep mistaking motion for progress.',
  ],
  downloadLabel: 'Download the Report',
  /**
   * The background plate — the live hero's own artwork, sent on 2026-08-28 without the
   * lettering the cover carries. The compass sits right of centre in it and the left
   * third is close to empty, which is why the words go left; that is how the plate was
   * composed and it is the reason a scrim is enough rather than a crop.
   *
   * `alt` describes the picture and not the report, because this one is decorative in a
   * way the page's other four are not: it carries no information the headline beside it
   * does not already carry.
   */
  photo: {
    src: '/photo/top-10-trends-2026-compass.webp',
    alt: 'A glowing golden compass at the centre of a swirl of blue data particles',
  } satisfies Photo,
  check:
    'The blue line under the title is the report’s subtitle, transcribed from the capture. The paragraph under it is not on the live hero at all; the live page runs the title, the subtitle and the button and nothing else. It is written here to give the page and its search result an opening sentence, and `prerender.tsx` takes the meta description from it. Say if it should come out, or send the sentence you want there.',
}

/**
 * The compass card — the centred band directly under the hero.
 *
 * Six short lines on the live page rather than paragraphs, with three phrases set bold
 * inside them. `strong` names those phrases so the component can emphasise them without
 * markup in the string; `emphasis` is the pull-quote line, which the live page sets bold
 * and centred on its own.
 */
export const TRENDS_COMPASS = {
  heading: ['Navigating the Noise:', 'Your Compass for 2026'],
  opening: ['2026 is not about chasing more technology.', 'It is about calibration, clarity, and disciplined execution.'],
  /** The line with "explosion" and "noise" set bold inside it. */
  question: {
    before: 'If 2024 was the ',
    firstStrong: 'explosion',
    middle: ' and 2025 the ',
    secondStrong: 'noise',
    after: ', 2026 is the year organizations finally ask the right question:',
  },
  emphasis: '“Is this creating measurable value?”',
  closing: 'Don’t guess your future. Measure it.',
  outro: 'Discover the trends shaping 2026 and learn how to turn them into a focused, executable roadmap.',
  check:
    'Every line in this band is transcribed from the reference capture, including where the bold falls: “explosion”, “noise” and the quoted question. It wants reading back against digitopia.co.',
}

/**
 * "From AI Chaos to Measured Value" — words left, a report mockup right.
 *
 * The live band prints five short paragraphs with a phrase set bold at the end of three
 * of them. Each paragraph is split into the plain opening and the bold tail so the
 * component can set it without markup in the string; a `strong` of `undefined` is a
 * paragraph with no emphasis in it.
 */
interface EmphasisedLine {
  /** The plain opening of the line. */
  body: string
  /** The phrase the live page sets bold, where there is one. */
  strong?: string
  /** Whatever follows the bold phrase — usually the full stop. */
  after?: string
}

/**
 * The five lines, typed rather than inferred: two of them carry no `strong`, and an
 * inferred array of those two literal shapes is a union the component cannot read
 * `.strong` off at all.
 */
const CHAOS_PARAGRAPHS: EmphasisedLine[] = [
  {
    body: 'We are entering 2026 in a VUCA world on steroids. The velocity of change is overwhelming, and many organizations are experiencing ',
    strong: 'AI fatigue instead of AI impact',
    after: '.',
  },
  {
    body: 'At Digitopia, we believe complexity is not the enemy. ',
    strong: 'Unmeasured complexity is',
    after: '.',
  },
  {
    body: 'This report cuts through the hype and highlights ',
    strong: '10 trends that truly matter in 2026',
    after: '.',
  },
  { body: 'Not all trends require immediate action.' },
  { body: 'But all require ', strong: 'clarity, prioritization, and measurement', after: '.' },
]

export const TRENDS_CHAOS = {
  heading: 'From AI Chaos to Measured Value',
  paragraphs: CHAOS_PARAGRAPHS,
  /**
   * One of the three mockups supplied with this request — the portrait shot of the
   * report held in a hand against a dark curtain. The reference renders this half of the
   * band empty, so the pairing is ours; see `check`.
   */
  photo: {
    src: '/photo/top-10-trends-2026-in-hand.webp',
    alt: 'A hand holding a printed copy of the Top 10 Trends 2026 report against a dark curtain',
  } satisfies Photo,
  check:
    'The five lines are transcribed from the reference capture, bold included, and want reading back against digitopia.co. The image beside them is the pairing question this page has to ask three times: the capture renders all three of this page’s image slots empty, so the three mockups you sent are matched to slots by shape: the portrait in-hand shot here, the small on-a-table shot beside the audience accordion, and the wide two-copy mockup beside the contents list. Say if the live page pairs them differently, or carries no image in one of the three.',
}

/**
 * The three recommendations, as three cards across.
 *
 * Each is one sentence on the live page with its opening set bold and closed by a colon
 * — "**Measure before you move:** Establish a clear baseline…". Split into `title` and
 * `body` here so the label can be set rather than emphasised inside a sentence, which is
 * the call every designed report page on this site makes.
 */
export const TRENDS_RECOMMENDATIONS = {
  heading: 'Three Essential Recommendations',
  items: [
    {
      title: 'Measure before you move',
      body: 'Establish a clear baseline of your Digital and AI maturity so every decision is driven by evidence, not noise.',
    },
    {
      title: 'Prioritize ruthlessly',
      body: 'Turn trends into a short, ranked roadmap with owners and success metrics, and say no to everything that does not move the needle.',
    },
    {
      title: 'Execute as one team',
      body: 'Run transformation as a cross-functional team sport with shared accountability and regular recalibration.',
    },
  ],
  check:
    'The heading and all three sentences are transcribed from the reference capture. The one change is presentational and it is the house call: the live page runs each card as prose with its opening bolded inside the sentence, as in “**Measure before you move:** Establish a clear baseline…”, and this page sets that label on its own line above the sentence, the way every designed report page here does. Nothing else about the three has changed.',
}

/**
 * "Tailored for Executives" — a mockup left, an accordion of audiences right.
 *
 * **Four of these five blurbs are written here rather than transcribed.** The capture
 * shows only the first panel open, and the other four are not recoverable from it. They
 * are written in the register of the one that is real, and `check` says so.
 */
export const TRENDS_AUDIENCE = {
  heading: 'Tailored for Executives',
  intro: {
    before: 'If you are responsible for ',
    strong: 'deciding what not to do',
    after: ', this report is for you.',
  },
  items: [
    {
      // The one panel the reference capture has open, so the one sentence here that is
      // digitopia.co's rather than ours.
      title: 'C-suite Executives',
      body: 'CEOs, CIOs, and business leaders shaping long-term strategies.',
    },
    {
      title: 'Innovation Leaders',
      body: 'Teams deciding which emerging technologies deserve a pilot, and which do not.',
    },
    {
      title: 'Strategy and Operations Teams',
      body: 'The people turning direction into a ranked roadmap with owners and success metrics.',
    },
    {
      title: 'IT and Technology Leaders',
      body: 'Architects and CTOs weighing agents, data platforms, sovereignty and cyber trust.',
    },
    {
      title: 'Sustainability Advocates',
      body: 'Leaders holding transformation to measurable environmental and social outcomes.',
    },
  ],
  downloadLabel: 'Download the Report',
  /**
   * The smallest of the three mockups supplied with this request — the report lying on a
   * marble side table. The reference renders this half of the band empty, so the pairing
   * is ours; `TRENDS_CHAOS.check` is where all three pairings are asked about.
   */
  photo: {
    src: '/photo/top-10-trends-2026-on-table.webp',
    alt: 'A printed copy of the Top 10 Trends 2026 report lying on a marble side table',
  } satisfies Photo,
  check:
    'This is the band with the real gap in it. The reference capture shows the accordion with only “C-suite Executives” open, so the only sentence digitopia.co supplies is “CEOs, CIOs, and business leaders shaping long-term strategies.” The four titles beside it are the live ones, but their sentences are closed in the capture and could not be read off it, so the four blurbs under Innovation Leaders, Strategy and Operations Teams, IT and Technology Leaders and Sustainability Advocates are written here, in the register of the first. Send the live wording and they are four one-line edits. The accordion itself opens and closes with no JavaScript, because these pages are prerendered and never hydrated, and the first panel is open on load, as it is on the live page.',
}

/**
 * "What's Inside the 2026 Report?" — words left, the two-copy mockup right.
 */
export const TRENDS_INSIDE = {
  heading: 'What’s Inside the 2026 Report?',
  intro: 'Across strategy, AI, operations, and governance, the report explores:',
  items: [
    'The shift from fast AI to deep, reasoning intelligence',
    'The rise of Agent Marketplaces and digital labor',
    'Why AI Sovereignty is now a board-level topic',
    'How cyber trust replaces perimeter security',
    'Why AI maturity beats AI ambition',
    'When to act now and when to stay in radar mode',
  ],
  outro: 'Each trend answers one critical question:',
  emphasis: 'What should leaders do about this now?',
  /** The wide two-copy mockup supplied with this request. */
  photo: {
    src: '/photo/top-10-trends-2026-mockup.webp',
    alt: 'Two copies of the Top 10 Trends 2026 report, one standing in front of the other',
  } satisfies Photo,
  check:
    'The heading, the lead-in, all six bullets and the closing question are transcribed from the reference capture and want reading back against digitopia.co. Note that the list is six lines under a heading that promises ten trends. That is the live page’s own shape, a sample rather than the table of contents, so it is left as it is.',
}

/**
 * The band pointing at the maturity report.
 *
 * **The one band that is not on the live page, and the only change in this request that
 * was asked for in words rather than shown in a picture.** The instruction with the
 * captures was *"en aşağıda önceden konuştuğumuz gibi 2024'e değil 2026 DAIMI report'a
 * yönlendirelim"* — the same instruction that has now arrived with eleven report pages —
 * so the CTA, the cover and the sentence all name the Digital & AI Maturity Report 2026,
 * which is the report this site actually publishes.
 *
 * It is the global 2026 edition rather than an Industry Focus cut, because this report
 * is not about one sector. That is the only thing about it `check` still asks.
 */
export const TRENDS_MATURITY = {
  heading: 'How Does Your Digital Maturity Compare?',
  body: 'The first recommendation in this report is to measure before you move. The Digital & AI Maturity Report 2026 is where that baseline comes from: it shows where organizations stand today across six dimensions, how leaders, average performers and laggards separate, and what the gap between AI ambition and AI capability actually costs. Compare your own position before you decide which of the ten trends to act on.',
  ctaLabel: 'Explore the Digital & AI Maturity Report 2026',
  ctaHref: `/en/impact-labs/reports/${DAIMI_SLUG}`,
  photo: {
    src: '/photo/daimi-report-2026-cover.webp',
    alt: 'Digital & AI Maturity Report 2026 cover',
  } satisfies Photo,
  check:
    'This whole band is an addition rather than a transcription: the live Top 10 Trends page has nothing between the contents list and the closing form. It is here because you asked for the 2026 DAIMI pointer at the bottom of the page rather than the 2024 report, and because this report’s own first recommendation is to establish a baseline of Digital and AI maturity, which is what that report is. It points at the global 2026 edition rather than at an Industry Focus cut, since Top 10 Trends is not about one sector. Two things to decide: the paragraph is written here, not supplied, so send different words if you want them; and say if the band should come out altogether and the 2026 pointer live only in the closing band instead.',
}

/**
 * The closing band, which is where the live page's gated form sits.
 */
export const TRENDS_CLOSE = {
  heading: 'Turn Trends Into Action',
  body: 'Download the report and start building a prioritized, measurable transformation roadmap for 2026.',
  expertLabel: 'Talk to an expert',
  // `#contact` rather than the Contact Us page: the form sits at the foot of
  // this very page, so the button scrolls instead of navigating away.
  expertHref: '#contact',
  check:
    'The heading and the sentence are the live band’s. What this page cannot reproduce is the form under them: on digitopia.co this band is a HubSpot form asking for first and last name, business email, company, job title, phone, country, industry, a marketing opt-in and the privacy statement, and the PDF arrives afterwards. These report pages are prerendered with no JavaScript, so the form cannot be embedded here, and every Download button on this page links straight to the PDF in our own bucket, which is what the other thirty reports on this site do. Say if this report should be gated behind the form instead.',
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy checks in —
 * the same mechanism `outlookEnergyChecks()` uses, and appended to the same list in
 * `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file. This page arrived
 * with all three of its images. What is left is transcription nobody has read against
 * the original, four audience blurbs written rather than transcribed, three image
 * pairings the capture could not settle, one band that is an addition, and the gating
 * decision.
 */
export function topTrends2026Checks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: TRENDS_INTRO.check },
    { section: 'compass', check: TRENDS_COMPASS.check },
    { section: 'chaos', check: TRENDS_CHAOS.check },
    { section: 'recommendations', check: TRENDS_RECOMMENDATIONS.check },
    { section: 'audience', check: TRENDS_AUDIENCE.check },
    { section: 'inside', check: TRENDS_INSIDE.check },
    { section: 'maturity', check: TRENDS_MATURITY.check },
    { section: 'download', check: TRENDS_CLOSE.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${TRENDS_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
